import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  addBrand,
  addDeviceCategory,
  addDeviceType,
  addIp,
  alarmsQuery,
  autoDecommissionQuery,
  brandsQuery,
  categoriesQuery,
  changeAlarmStatus,
  changeAutoDecommissionStatus,
  changeBrandStatus,
  changeCategoryStatus,
  changeMonitoringStatus,
  changeSubTypeStatus,
  changeTypeStatus,
  createAlarm,
  deviceTypesQuery,
  editDeviceCategory,
  endpointIpsQuery,
  endpointQuery,
  formBuilderQuery,
  getAlarms,
  inventoreeShellQuery,
  isInventoreeTab,
  licensesQuery,
  licensesTableQuery,
  loadDefaultSettings,
  newLicense,
  performanceScoreQuery,
  priceChange,
  rebuildConfiguration,
  removeAlarm,
  removeBrand,
  removeCategory,
  removeIp,
  removeType,
  saveBuildForm,
  saveImport,
  savePerformanceScore,
  saveSubtype,
  subdeviceTypesQuery,
  subtypeListQuery,
  typesToImportQuery,
  updateAlarm,
  updateEndpoint,
  type InventoreeTab,
} from '../../api/settingsInventoree'
import { escapeHtml } from '../../inventoree/format'
import { newAlert, toast } from '../../ui/notify'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
import { LoadFailed } from '../../ui/LoadFailed'
import './InventoreeSettingsPage.css'
import { InvCheck, InvHead, InvPicker, InvPickerHeader, InvPickerItem } from './InventoreeParts'
import { useState } from 'react'

/**
 * Settings → Inventoree: tabList.php + fiecare ecran din
 * inventoree_settings/, element cu element.
 *
 * „Relation rules" ramane o fila EXTERNA: e o pagina a ei (`/app/settings/
 * ci_rules`, `settings/ci_rules` in portal), nu un panou al hub-ului asta -
 * asa e si in portal, unde fila incarca un fragment de la alta adresa.
 */

export function InventoreeSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(inventoreeShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isInventoreeTab(tab)) return
    const first = shell.data.tabs.find((t) => !t.external)
    if (first) void navigate({ to: '/settings/inventoree/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const current = isInventoreeTab(tab) ? tab : (shell.data.tabs.find((t) => !t.external)?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Inventoree</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'brands' && <BrandsPanel />}
        {current === 'device_types' && <DeviceTypesPanel />}
        {current === 'device_auto_decommissioned' && <AutoDecommissionPanel />}
        {current === 'subdevice_types' && <SubtypesPanel />}
        {current === 'device_categories' && <CategoriesPanel />}
        {current === 'type_form_builder' && <FormBuilderPanel />}
        {current === 'softwares_licenses' && <LicensesPanel />}
        {current === 'performance_score' && <ScorePanel />}
        {current === 'alarms' && <AlarmsPanel />}
        {current === 'endpoint_status_settings' && <EndpointPanel />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: InventoreeTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const href = tab.external ? '/' + tab.href.replace(/^\//, '') : '/app/settings/inventoree/' + tab.id
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (tab.external) return
    event.preventDefault()
    void navigate({ to: '/settings/inventoree/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.anchor} title={tab.title} href={href} data-toggle={tab.id === 'alarms' ? 'tab' : undefined} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

function num(row: Record<string, unknown>, key: string): number {
  return Number(row[key] ?? 0)
}

function str(row: Record<string, unknown>, key: string): string {
  return String(row[key] ?? '')
}

/**
 * Un obiect din raspuns, care poate sa nu vina deloc.
 *
 * `connections` a venit `null` de la capat pentru o firma care nu si-a salvat
 * niciodata setarile de endpoint, iar `connections.connection_network` a oprit
 * ecranul cu "Something went wrong!". Citit prin `obj()`, un camp care lipseste
 * e cel mult un zero.
 */
function obj(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

function BrandsPanel() {
  const query = useQueryClient()
  const page = useQuery(brandsQuery())
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const brands = page.data.brands
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'brands'] })
  return (
    <>
      <div id="content">
        <InvHead
          strong="Devices"
          rest="brands"
          subtitle="manage brands"
          tools={
            <button className="btn btn-success btn-sm" data-toggle="modal" data-target="#myModal" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle"></i> Add brand
            </button>
          }
        />
        <ul className="list-group od-inv-list">
          {brands.length === 0 ? (
            <li className="list-group-item" style={{ padding: 0, border: 0 }}>
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i> No brands yet.
              </div>
            </li>
          ) : (
            brands.map((brand, index) => (
              <li className="list-group-item od-inv-row" key={num(brand, 'ID')}>
                <div className="od-inv-name">
                  <span className="badge">{index + 1}</span> {str(brand, 'brand_name')}
                </div>
                {/* Pastila consolei, nu `bg-red`: acolo scrisul consolei cadea
                    pe rosu, 1,24:1 masurat in Edge cu foile de productie. */}
                <div className="od-inv-count">
                  <span className="badge">{num(brand, 'devices')} devices</span>
                </div>
                <div className="od-inv-status">
                  <InvCheck
                    className="check"
                    label="Enabled"
                    checked={num(brand, 'disabled') === 0}
                    onChange={(on) => void changeBrandStatus(num(brand, 'ID'), on ? 1 : 0).then(reload)}
                  />
                </div>
                <div className="od-inv-actions">
                  <button
                    data-brand={num(brand, 'ID')}
                    className="btn btn-danger btn-xs removeBrand"
                    onClick={() => {
                      if (!confirm('Are you sure?')) return
                      void removeBrand(num(brand, 'ID')).then(reload)
                    }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      <Modal
        id="myModal"
        open={open}
        title="New brand"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary addBrandButton"
              onClick={() => {
                setOpen(false)
                void addBrand(name).then(() => {
                  setName('')
                  void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'brands'] })
                })
              }}
            >
              Save changes
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="addBrand" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="col-sm-3 control-label">Brand name</label>
            <div className="col-sm-7">
              <input className="form-control" type="text" name="brand_name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

/** Tipurile din catalogul CMDB - aceeasi lista pe care o scria portalul in `<datalist>`. */
const CMDB_TYPES = [
  'Desktop',
  'Laptop',
  'Workstation',
  'Thin Client',
  'Kiosk',
  'POS',
  'Server',
  'Virtual Server',
  'Compute Instance',
  'Network',
  'Storage',
  'Printer & Imaging',
  'Mobile',
  'Telecom',
  'Security Appliance',
  'IoT & OT',
  'Power & Infrastructure',
]

/*
 * Ce fac bifele de pe ecranul Device types, scris pentru om. Textele spun DOAR
 * ce face codul (hotararea din 5 octombrie 2026):
 *
 * - Enabled (`status`) e filtrul `status = 1` al listelor din care se alege
 *   un tip: Add device (`DbalInventoryMapSource::deviceTypes`, fara cadere pe
 *   tot catalogul), Edit device si editarea in masa, aparatele descoperite,
 *   ghicirea tipului la descoperire. Add device (`DeviceWrites::addDevice`)
 *   si importul CSV (`CsvImport::validate`) refuza un tip oprit. Listele de
 *   aparate NU se uita la el.
 * - Remote Monitoring (`remote_monitoring`) = tipurile care au voie in
 *   monitorizarea la distanta, prin releu (`RemoteMonitoringTypes`): lista
 *   Proactivitee "Network devices" si contorul ei, si poarta de la SNMP Hosts >
 *   Add. Aparatele deja urmarite NU se opresc - se marcheaza "Type not
 *   enabled". Furnizorul fara nicio linie aici cade pe catalog.
 */
const DEVICE_TYPES_HELP =
  'This is your list of device types. Each checkbox is saved for your organization and applies right away. Turning a type off never deletes it and never changes the devices that already have it.'

const ENABLED_HELP =
  'Enabled: the type is offered wherever a device type is picked - Add device, Edit device, bulk edit in the device list, classifying or adding discovered devices, and the automatic type guess of network discovery. Add device and CSV import refuse a type that is turned off. Unchecked, the type is only hidden from those lists: devices that already have it keep it and stay visible everywhere.'

const REMOTE_MONITORING_HELP =
  'Remote Monitoring: devices of this type may be watched from outside, without an agent, by a relay probe (Proactivitee > SNMP Hosts > Add), and they make up the Proactivitee "Network devices" list (servers excluded). Unchecked, devices of this type cannot be added to remote monitoring and leave the "Network devices" list; devices already watched keep being watched and are marked "Type not enabled".'

/**
 * "i"-ul de langa capul unei coloane: acelasi balon ca `Info` din
 * `devices/DeviceAgentModules.tsx` - `data-toggle="tooltip"`, pus de
 * `src/ui/tooltips.ts` - nu un al doilea fel de a explica ceva. `aria-label`
 * fiindca `tooltips.ts` goleste `title` la prima trecere cu mausul.
 */
function ColumnInfo({ what, text }: { what: string; text: string }) {
  return (
    <i
      className="fas fa-info-circle od-inv-i"
      data-toggle="tooltip"
      data-placement="top"
      data-help={what}
      title={text}
      role="img"
      aria-label={text}
    ></i>
  )
}

function DeviceTypesPanel() {
  const query = useQueryClient()
  /*
   * Tipurile dezactivate nu se sterg - sunt aparate care le poarta - dar nu
   * stau in fata cuiva care cauta un tip viu. Se cer cu bifa asta, si serverul
   * hotaraste ce trimite: filtrul e acolo, nu aici.
   */
  const [showDisabled, setShowDisabled] = useState(false)
  const page = useQuery(deviceTypesQuery(showDisabled))
  const imports = useQuery({ ...typesToImportQuery(), enabled: false })
  const [addOpen, setAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [typeName, setTypeName] = useState('')
  const [picked, setPicked] = useState<number[]>([])
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const types = page.data.types
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'device-types'] })
  const importable = imports.data?.importTypes ?? []
  return (
    <>
      <div id="content">
        {/*
         * Capul ca pe Customers: uneltele plutite la dreapta INAINTEA titlului.
         * Inainte stateau intr-o bara `pull-right` dupa subtitlu, iar plutirea
         * cobora 4px peste primul rand din lista si ii impingea bifele pe
         * randul urmator.
         */}
        <InvHead
          strong="Devices"
          rest="types"
          subtitle="devices types management system"
          tools={
            <>
              <InvCheck id="showDisabledTypes" label="Show disabled" checked={showDisabled} onChange={setShowDisabled} />
              <button className="btn btn-success btn-sm" data-toggle="modal" data-target="#myModal" onClick={() => setAddOpen(true)}>
                <i className="fas fa-plus-circle"></i> Add type
              </button>
              <button
                className="btn btn-primary btn-sm"
                data-toggle="modal"
                data-target="#myImportModal"
                onClick={() => {
                  setImportOpen(true)
                  void imports.refetch()
                }}
              >
                <i className="fas fa-exchange-alt"></i> Import types
              </button>
            </>
          }
        />
        <div className="notifications"></div>
        <p className="od-inv-help" id="deviceTypesHelp">
          <strong>What these settings do.</strong> {DEVICE_TYPES_HELP}
        </p>
        <ul className="list-group od-inv-list">
          {/* Capul coloanelor, cu "i"-ul fiecarei bife. Butonul ascuns din
              dreapta tine locul lui Remove, ca coloanele sa cada la acelasi x
              ca pe randuri. */}
          {types.length > 0 && (
            <li className="list-group-item od-inv-row od-inv-headrow">
              <div className="od-inv-name">Type</div>
              <div className="od-inv-count">Devices</div>
              <div className="od-inv-status">
                Enabled <ColumnInfo what="enabled" text={ENABLED_HELP} />
              </div>
              <div className="od-inv-monitor">
                Remote Monitoring <ColumnInfo what="remote_monitoring" text={REMOTE_MONITORING_HELP} />
              </div>
              <div className="od-inv-actions" aria-hidden="true">
                <span className="btn btn-xs" style={{ visibility: 'hidden' }}>
                  Remove
                </span>
              </div>
            </li>
          )}
          {types.length === 0 ? (
            <li className="list-group-item" style={{ padding: 0, border: 0 }}>
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i> No devices type. You can import existent types or create new ones.
              </div>
            </li>
          ) : (
            types.map((type, index) => (
              <li className="list-group-item od-inv-row" key={num(type, 'scheme_id') || num(type, 'ID')}>
                <div className="od-inv-name">
                  <span className="badge">{index + 1}</span> {str(type, 'name')}
                </div>
                {/* Pastila consolei (cerneala stinsa pe gri, 4,75:1), nu
                    `bg-red`: acolo scrisul consolei cadea pe rosu, 1,24:1. */}
                <div className="od-inv-count">
                  <span className="badge">{num(type, 'devices')} devices</span>
                </div>
                <div className="od-inv-status">
                  <InvCheck
                    className="check"
                    label="Enabled"
                    checked={num(type, 'status') === 1}
                    onChange={(on) => void changeTypeStatus(num(type, 'scheme_id'), on ? 1 : 0).then(reload)}
                  />
                </div>
                <div className="od-inv-monitor">
                  <InvCheck
                    className="checkMonitoring"
                    label="Remote Monitoring"
                    checked={num(type, 'remote_monitoring') === 1}
                    onChange={(on) => void changeMonitoringStatus(num(type, 'scheme_id'), on ? 1 : 0).then(reload)}
                  />
                </div>
                <div className="od-inv-actions">
                  <button
                    data-type={num(type, 'scheme_id')}
                    className="btn btn-danger btn-xs removeType"
                    onClick={() => {
                      if (!confirm('Are you sure?')) return
                      void removeType(num(type, 'scheme_id')).then(() => {
                        newAlert('<i class="fas fa-info-circle"></i> Device type was removed.', 'success', 5000)
                        reload()
                      })
                    }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      <Modal
        id="myModal"
        open={addOpen}
        title="New device type"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setAddOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary addTypeButton"
              onClick={() => {
                setAddOpen(false)
                void addDeviceType(typeName)
                  .then(() => {
                    newAlert('<i class="fas fa-info-circle"></i> Type was added succesfully.', 'success', 5000)
                    setTypeName('')
                    reload()
                  })
                  .catch((e: unknown) => {
                    /* Un tip din afara catalogului CMDB nu e o eroare de
                       server: serverul spune si CE se putea scrie, si asta se
                       arata omului intreg. */
                    const said = e instanceof ApiError && e.status === 400 ? e.message : 'Type could not be added.'
                    newAlert('<i class="fas fa-exclamation-triangle"></i> ' + said, 'danger', 12000)
                  })
              }}
            >
              Save changes
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="addDeviceType" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="col-sm-3 control-label">Type name</label>
            <div className="col-sm-7">
              <input
                className="form-control"
                type="text"
                name="type_name"
                required
                list="cmdbCanonicalTypes"
                placeholder="Must match CMDB catalog"
                value={typeName}
                onChange={(e) => setTypeName(e.target.value)}
              />
              <datalist id="cmdbCanonicalTypes">
                {CMDB_TYPES.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <p className="help-block">Only CMDB catalog names (Class → Type). Brands and models stay on the device sheet — not here.</p>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        id="myImportModal"
        open={importOpen}
        title="Import existent device types"
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setImportOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary importTypeButton"
              onClick={() => {
                if (picked.length === 0) {
                  newAlert('<i class="fas fa-exclamation-circle"></i> No type selected to import.', 'warning', 5000)
                  setImportOpen(false)
                  return
                }
                void saveImport(picked).then(() => {
                  setImportOpen(false)
                  setPicked([])
                  newAlert('<i class="fas fa-info-circle"></i> Types were succesfully imported.', 'success', 5000)
                  reload()
                })
              }}
            >
              Import
            </button>
          </>
        }
      >
        <form id="importTypesForm" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          {!imports.data ? (
            <Spinner />
          ) : importable.length === 0 ? (
            <div className="alert alert-info">Noting to import.</div>
          ) : (
            <ul className="list-group od-inv-list">
              <li className="list-group-item od-inv-row od-inv-group">
                <div className="od-inv-name">
                  <InvCheck
                    name="checkAll"
                    className="importCheckAll"
                    label="Check/Uncheck All"
                    checked={picked.length === importable.length}
                    onChange={(on) => setPicked(on ? importable.map((t) => num(t, 'ID')) : [])}
                  />
                </div>
              </li>
              {importable.map((type) => (
                <li className="list-group-item od-inv-row" key={num(type, 'ID')}>
                  <div className="od-inv-name">
                    <InvCheck
                      name="typeId[]"
                      className="importCheck"
                      value={num(type, 'ID')}
                      label={str(type, 'name')}
                      checked={picked.includes(num(type, 'ID'))}
                      onChange={(on) => setPicked((cur) => (on ? [...cur, num(type, 'ID')] : cur.filter((id) => id !== num(type, 'ID'))))}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </form>
      </Modal>
    </>
  )
}

function AutoDecommissionPanel() {
  const query = useQueryClient()
  const page = useQuery(autoDecommissionQuery())
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const types = page.data.types
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'auto-decommission'] })
  return (
    <div id="content">
      {/*
       * Capul era doua coloane `col-md-6` intr-un `header` cu `min-height:
       * 100px`, iar in a doua un buton plutit la dreapta si sub el o eticheta
       * plutita si ea, cu `clear: both`. Doua plutiri una sub alta intr-un cap
       * fara `row` in jur: titlul si butonul nu stateau pe aceeasi linie de
       * baza, iar cei 100px erau tinuti cu mana ca sa incapa plutirile.
       *
       * Acum capul e cel al zonei - unealta plutita INAINTEA titlului - iar
       * indemnul la reconstruire e un rand de text sub el, unde se si citeste.
       */}
      <InvHead
        strong="Devices"
        rest="types auto-decommissioned"
        subtitle="Devices will be automatically decommissioned if the agent has not been communicating in the last 3 months"
        tools={
          <form
            action=""
            method="post"
            id="rebuildInventoreeCacheForm"
            onSubmit={(e) => {
              e.preventDefault()
              void rebuildConfiguration().then(() => toast('Configuration build successfully.', 'Success'))
            }}
          >
            <button type="submit" id="refreshInventoreeCache" className="btn btn-sm btn-success">
              <i className="fas fa-cogs" aria-hidden="true"></i> Rebuild configuration
            </button>
          </form>
        }
      />
      <p className="color">
        Rebuild configuration after every change. New settings will take effect after you do the <strong>REBUILD</strong>.
      </p>
      <ul className="list-group od-inv-list">
        {types.length === 0 ? (
          <li className="list-group-item" style={{ padding: 0, border: 0 }}>
            <div className="alert alert-info">
              <i className="fas fa-info-circle"></i> No devices type. You can import existent types or create new ones from{' '}
              <a href="/app/settings/inventoree/device_types">Device type</a>.
            </div>
          </li>
        ) : (
          types.map((type, index) => (
            <li className="list-group-item od-inv-row" key={num(type, 'scheme_id') || num(type, 'ID')}>
              <div className="od-inv-name">
                <span className="badge">{index + 1}</span> {str(type, 'name')}
              </div>
              <div className="od-inv-status">
                <InvCheck
                  className="check"
                  label="Enabled"
                  checked={num(type, 'auto_decommissioned') === 1}
                  onChange={(on) => void changeAutoDecommissionStatus(num(type, 'scheme_id'), on ? 1 : 0).then(reload)}
                />
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

function SubtypesPanel() {
  const query = useQueryClient()
  const page = useQuery(subdeviceTypesQuery())
  const [showDisabled, setShowDisabled] = useState(false)
  const list = useQuery(subtypeListQuery(showDisabled))
  const [open, setOpen] = useState(false)
  const [priceOpen, setPriceOpen] = useState(false)
  const [parent, setParent] = useState('')
  const [name, setName] = useState('')
  const [cost, setCost] = useState('')
  const [currency, setCurrency] = useState('')
  const [subId, setSubId] = useState('')
  const [priceId, setPriceId] = useState('')
  const [priceType, setPriceType] = useState('category')
  const [price, setPrice] = useState('')
  const [priceCurrency, setPriceCurrency] = useState('')
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const categories = page.data.Categories
  const currencyOptions = page.data.Currencies.map((c) => ({ value: str(c, 'currency'), label: str(c, 'currency') }))
  const rows = list.data?.list ?? []
  return (
    <>
      <div id="content">
        <InvHead
          strong="Devices"
          rest="sub-types"
          subtitle="Devices sub-types management system"
          tools={
            <>
              {/* Filtrul e pe SERVER, ca la Devices types: bifa doar cere
                  altceva, si ecranul arata ce vine. */}
              <InvCheck id="showDisabledSubtypes" label="Show disabled (legacy)" checked={showDisabled} onChange={setShowDisabled} />
              <button
                className="btn btn-success btn-sm addChange-subcategory"
                onClick={() => {
                  setSubId('')
                  setParent('')
                  setName('')
                  setCost('')
                  setCurrency('')
                  setOpen(true)
                }}
              >
                <i className="fas fa-plus-circle"></i> Add sub-type
              </button>
            </>
          }
        />
        <div className="notifications"></div>
        <ul className="list-group od-inv-list">
          {rows.map((obj) => (
            <SubtypeBlock
              key={num(obj, 'ID')}
              obj={obj}
              onPrice={(id, type, estimate, cur) => {
                setPriceId(String(id))
                setPriceType(type)
                setPrice(String(estimate))
                setPriceCurrency(cur)
                setPriceOpen(true)
              }}
              onEdit={(sub) => {
                setSubId(String(num(sub, 'ID')))
                setParent(String(num(sub, 'device_type_id')))
                setName(str(sub, 'name'))
                setCost(str(sub, 'estimate_unit_cost'))
                setCurrency(str(sub, 'currency'))
                setOpen(true)
              }}
              onStatus={(id) =>
                void changeSubTypeStatus(id).then(() => query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'subtype-list'] }))
              }
            />
          ))}
        </ul>
      </div>
      <Modal
        id="myModal"
        open={open}
        title="New sub-device type"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary addEditDeviceSubType"
              onClick={() => {
                const fields: Record<string, string> = {
                  parent_type: parent,
                  subtype_name: name,
                  estimated_cost: cost,
                  currency,
                }
                if (subId !== '') fields.deviceSubTypeID = subId
                void saveSubtype(fields).then((res) => {
                  if (res.status === 'succcess') {
                    setOpen(false)
                    void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'subtype-list'] })
                  } else {
                    newAlert(res.message ?? 'Invalid data!', 'danger', 5000)
                  }
                })
              }}
            >
              Save changes
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="addSubDeviceType" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          <input type="hidden" name="deviceSubTypeID" value={subId} id="deviceSubType_id" />
          <div className="form-group">
            <label className="col-sm-3 control-label">Parent category</label>
            <div className="col-sm-7">
              {/* bootstrap-select, ca listele din setarile noi - fara
                  `form-control` pe invelis, altfel iese cutie in cutie. */}
              <SelectPicker
                id="deviceSubType_parent_type"
                name="parent_type"
                title="Select..."
                value={parent}
                onChange={setParent}
                options={categories.map((category) => ({ value: String(num(category, 'ID')), label: str(category, 'name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Sub-type name</label>
            <div className="col-sm-7">
              <input className="form-control" type="text" name="subtype_name" id="deviceSubType_subtype_name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Estimated cost</label>
            <div className="col-sm-7">
              <input className="form-control" name="estimated_cost" id="deviceSubType_estimated_cost" value={cost} onChange={(e) => setCost(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Currency</label>
            <div className="col-sm-7">
              <SelectPicker id="deviceSubType_currency" name="currency" title="Select..." value={currency} onChange={setCurrency} options={currencyOptions} />
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        id="PriceChangeModal"
        open={priceOpen}
        title="Change category price"
        labelledBy="PriceChangeModalLabel"
        onClose={() => setPriceOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setPriceOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary changePriceButton"
              onClick={() => {
                void priceChange({ id: priceId, type: priceType, price, currency: priceCurrency }).then((res) => {
                  if (res.status === 'success') {
                    toast(res.message ?? 'Price updated!', 'Success')
                    setPriceOpen(false)
                    void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'subtype-list'] })
                  } else {
                    toast(res.message ?? 'Price not updated!', 'Error', 'error')
                  }
                })
              }}
            >
              Save changes
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="changePrice" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          <input type="hidden" name="id" id="changePrice-id" value={priceId} />
          <input type="hidden" name="type" value={priceType} />
          <div className="form-group">
            <label className="col-sm-3 control-label">Price</label>
            <div className="col-sm-7">
              <input type="text" name="price" className="form-control" value={price} id="priceChange-price" onChange={(e) => setPrice(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Currency</label>
            <div className="col-sm-7">
              <SelectPicker id="priceChange-currency" name="currency" title="Select..." value={priceCurrency} onChange={setPriceCurrency} options={currencyOptions} />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

/** Pretul estimat, cum il arata randul: suma si moneda, sau o linie cand nu e pus. */
function priceText(row: Record<string, unknown>): string {
  const text = `${str(row, 'estimate_unit_cost')} ${str(row, 'currency')}`.trim()
  return text === '' ? '—' : text
}

/*
 * O categorie si sub-tipurile ei, pe acelasi set de coloane: numele ia ce
 * ramane, pretul are coloana lui aliniata la dreapta, iar butoanele stau intr-o
 * coloana de latime fixa. Inainte fiecare rand isi plutea butoanele la dreapta
 * cu `margin-right` scris pe loc, deci pretul "700 EUR" al categoriei si
 * "400 EUR" al sub-tipului cadeau la x diferite, dupa cat de lat era butonul de
 * langa.
 */
const SUBTYPE_ACTIONS = { minWidth: 136 }

function SubtypeBlock({
  obj,
  onPrice,
  onEdit,
  onStatus,
}: {
  obj: Record<string, unknown>
  onPrice(id: number, type: string, cost: unknown, currency: string): void
  onEdit(sub: Record<string, unknown>): void
  onStatus(id: number): void
}) {
  const subs = (Array.isArray(obj.subType) ? obj.subType : []) as Array<Record<string, unknown>>
  return (
    <>
      <li className="list-group-item od-inv-row od-inv-group">
        <div className="od-inv-name">{str(obj, 'name')}</div>
        <div className="od-inv-price">{priceText(obj)}</div>
        <div className="od-inv-actions" style={SUBTYPE_ACTIONS}>
          <button
            className="btn btn-xs btn-primary change-price"
            data-id={num(obj, 'ID')}
            data-type="category"
            data-cost={str(obj, 'estimate_unit_cost')}
            data-currency={str(obj, 'currency')}
            onClick={() => onPrice(num(obj, 'ID'), 'category', obj.estimate_unit_cost, str(obj, 'currency'))}
          >
            Edit Price
          </button>
        </div>
      </li>
      {subs.map((sub) => (
        <li className="list-group-item od-inv-row" key={num(sub, 'ID')}>
          <div className="od-inv-name" style={{ paddingLeft: 16 }}>
            <i className="fa fa-share fa-flip-vertical" aria-hidden="true"></i>
            {str(sub, 'name')}
          </div>
          <div className="od-inv-price">{priceText(sub)}</div>
          <div className="od-inv-actions" style={SUBTYPE_ACTIONS}>
            <button
              className="btn btn-xs btn-primary addChange-subcategory"
              data-id={num(sub, 'ID')}
              data-type="subcategory"
              data-parent={num(sub, 'device_type_id')}
              data-name={str(sub, 'name')}
              data-cost={str(sub, 'estimate_unit_cost')}
              data-currency={str(sub, 'currency')}
              onClick={() => onEdit(sub)}
            >
              Edit
            </button>
            {/*
              * Aprins / stins, ca in portalul vechi: buton pe FIECARE rand,
              * rosu "Disable" cat timp sub-tipul e pornit, verde "Enable" cand
              * e stins. Bifa zonei, incercata la 26.9.16.21, nu se citea ca o
              * comanda. Acelasi cap ca pana acum (`subdevice-types/status?id=`),
              * care doar intoarce starea.
              */}
            <button
              className={num(sub, 'status') === 0 ? 'btn btn-xs btn-success change-status' : 'btn btn-xs btn-danger change-status'}
              data-id={num(sub, 'ID')}
              onClick={() => onStatus(num(sub, 'ID'))}
            >
              {num(sub, 'status') === 0 ? 'Enable' : 'Disable'}
            </button>
          </div>
        </li>
      ))}
    </>
  )
}

function CategoriesPanel() {
  const query = useQueryClient()
  const page = useQuery(categoriesQuery())
  const [addOpen, setAddOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [name, setName] = useState('')
  const [picked, setPicked] = useState<number[]>([])
  const [editId, setEditId] = useState(0)
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const categories = page.data.categories
  const typeOptions = page.data.deviceTypes.map((type) => ({ value: String(num(type, 'ID')), label: str(type, 'Nume') }))
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'categories'] })
  /*
   * "Device types" din cele doua ferestre. Inainte era `<select
   * class="selectpicker" multiple>` fara pluginul care il deseneaza: `app.css`
   * ascunde `.selectpicker` pana il inlocuieste bootstrap-select, deci campul
   * avea 0px - fereastra arata doar eticheta. Acum e `SelectPicker`, cu cautare,
   * iar `<select>`-ul ascuns duce mai departe `device_types[]`.
   */
  const typesField = (id: string) => (
    <SelectPicker
      id={id}
      name="device_types[]"
      title="Select.."
      multiple
      selectedTextFormat="count > 3"
      value={picked.map(String)}
      onChange={(next) => setPicked(next.map(Number).filter((n) => n > 0))}
      options={typeOptions}
    />
  )
  return (
    <>
      <div id="content">
        <InvHead
          strong="Devices"
          rest="categories"
          subtitle="devices categories management system"
          tools={
            <button
              className="btn btn-success btn-sm"
              data-toggle="modal"
              data-target="#addCategoryModal"
              onClick={() => {
                setName('')
                setPicked([])
                setAddOpen(true)
              }}
            >
              <i className="fas fa-plus-circle"></i> Add category
            </button>
          }
        />
        <div className="notifications"></div>
        <ul className="list-group od-inv-list">
          {categories.length === 0 ? (
            <li className="list-group-item" style={{ padding: 0, border: 0 }}>
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i> No devices categories. You can create new ones.
              </div>
            </li>
          ) : (
            categories.map((category, index) => (
              <li className="list-group-item od-inv-row" key={num(category, 'ID')}>
                <div className="od-inv-name">
                  <span className="badge">{index + 1}</span> {str(category, 'name')}
                </div>
                <div className="od-inv-count">
                  <span className="badge">{num(category, 'types')} device types</span>
                </div>
                <div className="od-inv-status">
                  <InvCheck
                    className="check"
                    label="Enabled"
                    checked={num(category, 'status') === 1}
                    onChange={(on) => void changeCategoryStatus(num(category, 'ID'), on ? 1 : 0).then(reload)}
                  />
                </div>
                {/* Coloana de latime fixa si cand "Remove" lipseste (categoria
                    nu se poate sterge): "Edit" sta la acelasi x pe toate
                    randurile, nu 35px mai la dreapta pe cele fara Remove. */}
                <div className="od-inv-actions" style={{ minWidth: 120 }}>
                  <button
                    data-category={num(category, 'ID')}
                    className="btn btn-primary btn-xs editCategory"
                    data-toggle="modal"
                    data-target="#editCategoryModal"
                    onClick={() => {
                      setEditId(num(category, 'ID'))
                      setName(str(category, 'name'))
                      setPicked([])
                      setEditOpen(true)
                    }}
                  >
                    Edit
                  </button>
                  {num(category, 'removable') === 1 ? (
                    <button
                      data-category={num(category, 'ID')}
                      className="btn btn-danger btn-xs removeCategory"
                      onClick={() => {
                        if (!confirm('Are you sure?')) return
                        void removeCategory(num(category, 'ID')).then(() => {
                          newAlert('<i class="fas fa-info-circle"></i> Device category was removed.', 'success', 5000)
                          reload()
                        })
                      }}
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="btn btn-xs" style={{ visibility: 'hidden' }} aria-hidden="true">
                      Remove
                    </span>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      <Modal
        id="addCategoryModal"
        open={addOpen}
        title="New device category"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setAddOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary saveCategory"
              onClick={() => {
                void addDeviceCategory(name, picked).then(() => {
                  setAddOpen(false)
                  reload()
                })
              }}
            >
              Save
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="saveDeviceCategory" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="col-sm-3 control-label">Category name</label>
            <div className="col-sm-7">
              <input className="form-control" type="text" name="category_name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Device types</label>
            <div className="col-sm-7 selectContainer">{addOpen && typesField('addCategoryTypes')}</div>
          </div>
        </form>
      </Modal>
      <Modal
        id="editCategoryModal"
        open={editOpen}
        title="Edit device category"
        labelledBy="editCategoryModalLabel"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setEditOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                void editDeviceCategory(editId, name, picked).then(() => {
                  setEditOpen(false)
                  reload()
                })
              }}
            >
              Save
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="editDeviceCategory" action="" method="post" onSubmit={(e) => e.preventDefault()}>
          <input type="hidden" name="category" value={editId} />
          <div className="form-group">
            <label className="col-sm-3 control-label">Category name</label>
            <div className="col-sm-7">
              <input className="form-control" type="text" name="category_name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Device types</label>
            <div className="col-sm-7 selectContainer">{editOpen && typesField('editCategoryTypes')}</div>
          </div>
        </form>
      </Modal>
    </>
  )
}

function FormBuilderPanel() {
  const query = useQueryClient()
  const page = useQuery(formBuilderQuery())
  const [type, setType] = useState(0)
  const [fields, setFields] = useState<Array<{ type: number; label: string; options: string[] }>>([])
  const [editOpen, setEditOpen] = useState(false)
  const [editIndex, setEditIndex] = useState(-1)
  const [editLabel, setEditLabel] = useState('')
  const [editOptions, setEditOptions] = useState('')
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  return (
    <div id="content">
      <InvHead
        strong="Type form"
        rest="builder"
        subtitle="the extra fields a device of this type carries, on top of the ones every device has"
      />
      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4>Select device type form to edit</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <form>
                    <div className="form-group">
                      <label className="col-sm-2 control-label">Device type</label>
                      <div className="col-sm-6 selectContainer">
                        {/*
                         * Lista lui bootstrap-select, FARA `form-control` pe
                         * invelis: clasa aia e `display: block` si `width: 100%`
                         * si le pune peste butonul care e deja un `btn-group`.
                         * `<select>`-ul ascuns duce mai departe `device_type`.
                         */}
                        <SelectPicker
                          id="device_type_form"
                          name="device_type"
                          title="Select..."
                          value={type ? String(type) : ''}
                          onChange={(next) => {
                            setType(Number(next) || 0)
                            setFields([])
                          }}
                          options={page.data.device_types.map((t) => ({ value: String(num(t, 'ID')), label: str(t, 'Nume') }))}
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="notifications"></div>
      <div className="row">
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4>Elements</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <div className="alert alert-info" style={{ marginBottom: 15 }}>
                    <i className="fas fa-info-circle"></i>
                    Drag an element into your form, then use <strong>Edit</strong> to give it a name. The names below describe the kind of field, not the question it asks &mdash; a <em>Yes / No choice</em> becomes something like &ldquo;Under warranty&rdquo; once you rename it.
                  </div>
                  <form className="form-elements">
                    <div className="form-group" data-field-type="1">
                      <label htmlFor="exampleInputEmail1">Text input</label>
                      <input className="form-control" id="exampleInputEmail1" type="text" onFocus={() => setFields((f) => [...f, { type: 1, label: 'Text input', options: [] }])} />
                    </div>
                    <div className="form-group" data-field-type="2">
                      <label htmlFor="exampleInputPassword1">Select</label>
                      <select className="form-control" onClick={() => setFields((f) => [...f, { type: 2, label: 'Select', options: ['Option 1', 'Option 2', 'Option 3'] }])}>
                        <option value="option 1">Option 1</option>
                        <option value="option 2">Option 2</option>
                        <option value="option 3">Option 3</option>
                      </select>
                    </div>
                    <div className="form-group" data-field-type="3">
                      <label>Number</label>
                      <input className="form-control" type="number" step="any" onFocus={() => setFields((f) => [...f, { type: 3, label: 'Number', options: [] }])} />
                    </div>
                    <div className="form-group" data-field-type="4">
                      <label>Date</label>
                      <input className="form-control" type="date" onFocus={() => setFields((f) => [...f, { type: 4, label: 'Date', options: [] }])} />
                    </div>
                    <div className="form-group" data-field-type="5">
                      <label>Yes / No choice</label>
                      <select className="form-control" onClick={() => setFields((f) => [...f, { type: 5, label: 'Yes / No choice', options: [] }])}>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                    </div>
                    <div className="form-group" data-field-type="6">
                      <label>Long text</label>
                      <textarea rows={3} className="form-control" onFocus={() => setFields((f) => [...f, { type: 6, label: 'Long text', options: [] }])}></textarea>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4>Your form</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <form className="your-form">
                    {fields.map((field, i) => (
                      <div className="form-group" key={i} data-field-type={field.type}>
                        <label>{field.label}</label>{' '}
                        <button
                          type="button"
                          className="btn btn-xs btn-primary"
                          onClick={() => {
                            setEditIndex(i)
                            setEditLabel(field.label)
                            setEditOptions(field.options.join('\n'))
                            setEditOpen(true)
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={type < 1}
                      onClick={() =>
                        void saveBuildForm(type, fields)
                          .then(() => {
                            newAlert('<i class="fas fa-info-circle"></i> Form saved.', 'success', 5000)
                          })
                          .catch((e: unknown) => {
                            /* Formularul s-a salvat si cand oglinda lui in CMDB
                               pica (500 cu `cmdb_mirror`): serverul spune care
                               campuri n-au ajuns, si asta se arata omului. */
                            const said =
                              e instanceof ApiError && e.status === 500 && e.message !== ''
                                ? escapeHtml(e.message)
                                : 'Form could not be saved.'
                            newAlert('<i class="fas fa-exclamation-triangle"></i> ' + said, 'danger', 12000)
                          })
                          .finally(() => {
                            void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'form-builder', type] })
                          })
                      }
                    >
                      Save
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        id="editFieldModal"
        open={editOpen}
        title="Edit field"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setEditOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary saveFormChanges"
              onClick={() => {
                setFields((cur) => cur.map((f, i) => (i === editIndex ? { ...f, label: editLabel, options: editOptions.split('\n').filter(Boolean) } : f)))
                setEditOpen(false)
              }}
            >
              Save changes
            </button>
          </>
        }
      >
        <form className="form-horizontal" id="formEditor" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="col-sm-2 control-label" htmlFor="field_label">
              Label
            </label>
            <div className="col-sm-10">
              <input type="text" className="form-control" name="field_label" id="field_label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
          </div>
          <div className="form-group formOptions">
            <label className="col-sm-2 control-label" htmlFor="field_options">
              Options
            </label>
            <div className="col-sm-10">
              <textarea rows={10} name="field_options" className="form-control" id="field_options" value={editOptions} onChange={(e) => setEditOptions(e.target.value)}></textarea>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}

const LICENSE_TABS = [
  { id: 0, label: 'No license applications' },
  { id: 1, label: 'OEM' },
  { id: 2, label: 'FPP Retail (Fully Packaged Product)' },
  { id: 3, label: 'SPLA (Service Provider License Agreement)' },
  { id: 4, label: 'USL (User Subscription License)' },
  { id: 5, label: 'VL (Volume Licensing)' },
  { id: 6, label: 'OVS (Open Value Subscription)' },
  { id: 7, label: 'GGWA (Get Genuine Windows Agreement)' },
  { id: 8, label: 'GGK (Get Genuine Kit)' },
]

function LicensesPanel() {
  const query = useQueryClient()
  const page = useQuery(licensesQuery())
  const [selected, setSelected] = useState(0)
  const table = useQuery(licensesTableQuery(selected))
  const [open, setOpen] = useState(false)
  const [licenseType, setLicenseType] = useState('1')
  const [software, setSoftware] = useState('')
  const [licensesNo, setLicensesNo] = useState('1')
  const [start, setStart] = useState('')
  const [valid, setValid] = useState('')
  const [period, setPeriod] = useState('1')
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const rows = table.data?.data ?? []
  return (
    <div id="content">
      <InvHead
        strong="Software"
        rest="licenses"
        subtitle="software licenses management"
        tools={
          <button className="btn btn-success btn-sm" data-toggle="modal" data-target="#addNewLicense" onClick={() => setOpen(true)}>
            <i className="fas fa-plus-circle"></i> Add license
          </button>
        }
      />
      <div className="row">
        <div className="col-md-3">
          {/*
           * Coloana de alegere a consolei (`od-inv-picker`), in locul filelor
           * `nav-metro-tabs`: alea veneau din tema veche, iar primul rand isi
           * scria albastrul INLINE (rgb(65,153,194)) - o culoare care nu e
           * nicaieri in zona. Randul ales se cunoaste dupa hartia lui si dupa
           * dunga de cerneala din stanga, ca in rail-ul de alaturi.
           *
           * Butonul "Add license" a plecat de aici in capul ecranului: era
           * `btn-block` deasupra listei si statea cu 34px PESTE ea.
           */}
          <InvPicker id="licenses-list">
            {LICENSE_TABS.map((tab) => (
              <InvPickerItem
                key={tab.id}
                label={tab.label}
                active={tab.id === selected}
                onPick={() => setSelected(tab.id)}
                liProps={{ 'data-license': tab.id }}
              />
            ))}
          </InvPicker>
        </div>
        <div className="col-md-9">
          <table className="table table-striped table-hover table-bordered dataTableM" width="100%" id="softwares-licenses-table">
            <thead>
              <tr>
                <th>Software</th>
                <th>Type</th>
                <th>Licenses</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={num(row, 'ID') || i} id={str(row, 'DT_RowId')}>
                  <td>{str(row, 'short_name')}</td>
                  <td>{str(row, 'type_name')}</td>
                  <td>{str(row, 'licenses_number')}</td>
                  <td>{str(row, 'status')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Modal
        id="addNewLicense"
        open={open}
        title="New license"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={() => setOpen(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary saveLicense"
              onClick={() => {
                const form = new FormData()
                form.set('company', String(page.data.company_id))
                form.set('license_type', licenseType)
                form.set('license_software', software)
                form.set('licenses_no', licensesNo)
                form.set('license_start_date', start)
                form.set('valid', valid)
                form.set('valid_period', period)
                void newLicense(form).then(() => {
                  setOpen(false)
                  void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'licenses-table'] })
                })
              }}
            >
              Save
            </button>
          </>
        }
      >
              <form className="form-horizontal labelcustomize" id="newLicenseForm" encType="multipart/form-data" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="" onSubmit={(e) => e.preventDefault()}>
                <input type="hidden" name="company" value={page.data.company_id} />
                <div className="form-group">
                  <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                    <span className="color">License type</span>
                  </label>
                  <div className="col-sm-9 selectContainer">
                    <SelectPicker
                      id="license_type"
                      name="license_type"
                      title="Select.."
                      value={licenseType}
                      onChange={setLicenseType}
                      options={LICENSE_TABS.filter((t) => t.id > 0).map((t) => ({ value: String(t.id), label: t.label }))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                    <span className="color">Software</span>
                  </label>
                  <div className="col-sm-9 selectContainer">
                    <SelectPicker
                      id="license_software"
                      name="license_software"
                      title="Select.."
                      value={software}
                      onChange={setSoftware}
                      options={page.data.softwares.map((row) => ({ value: String(num(row, 'ID')), label: str(row, 'short_name') }))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                    <span className="color">No. of licenses</span>
                  </label>
                  <div className="col-sm-9">
                    <input type="number" min={1} className="form-control" name="licenses_no" value={licensesNo} required onChange={(e) => setLicensesNo(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                    <span className="color">Start date</span>
                  </label>
                  <div className="col-sm-9">
                    <input type="text" className="form-control" id="datetimepicker" name="license_start_date" required value={start} onChange={(e) => setStart(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ textAlign: 'left' }} className="control-label col-md-3">
                    <span className="color">Valid license</span>
                  </label>
                  <div className="col-md-9">
                    <div className="input-group">
                      <input type="number" className="form-control" name="valid" value={valid} onChange={(e) => setValid(e.target.value)} />
                      <span className="input-group-addon">Period</span>
                      <select className="form-control" name="valid_period" value={period} onChange={(e) => setPeriod(e.target.value)}>
                        <option value="1">Days</option>
                        <option value="2">Months</option>
                        <option value="3">Years</option>
                        <option value="4">Lifetime</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                    <span className="color">Upload document</span>
                  </label>
                  <div className="col-sm-9">
                    <input type="file" className="form-control" name="license_document" />
                  </div>
                </div>
              </form>
      </Modal>
    </div>
  )
}

function ScorePanel() {
  const query = useQueryClient()
  const page = useQuery(performanceScoreQuery())
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const settings = page.data.settings
  const defaults = page.data.defaults
  const bands = page.data.bands
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const fields: Record<string, string> = {}
    form.forEach((v, k) => {
      fields[k] = String(v)
    })
    void savePerformanceScore(fields).then(() => query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'score'] }))
  }
  return (
    <>
      <InvHead strong="Performance score" rest="formula" subtitle="What counts as a good machine, for this service provider" />
      {!page.data.available && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-circle"></i>
          <strong>The settings table does not exist yet.</strong> Scoring runs on the built-in values, and the screen shows them below - but saving has nowhere to write until you run: <code>CREATE TABLE it_inventoree_score_settings</code> (see <code>docs/scor-de-configuratie.md</code>).
        </div>
      )}
      <form id="performanceScoreForm" action="" method="post" onSubmit={onSubmit}>
        <section className="settings">
          <div className="row">
            <div className="col-md-3">
              <h5 style={{ marginBottom: 20 }}>How much each one weighs</h5>
              <p className="color font-12">
                The numbers are relative to one another, not percentages: 30, 30, 25 and 15 mean exactly the same as 6, 6, 5 and 3. A zero drops the factor out of the calculation.
              </p>
            </div>
            <div className="col-md-9">
              <div className="row">
                {(
                  [
                    ['weight_cpu', 'Processor', 'cores times GHz'],
                    ['weight_ram', 'Memory', 'GB'],
                    ['weight_disk', 'Disk', 'solid state or not'],
                    ['weight_age', 'Age', 'from the BIOS date'],
                  ] as const
                ).map(([key, label, hint]) => (
                  <div className="col-md-3" key={key}>
                    <div className="form-group">
                      <label>{label}</label>
                      <input className="form-control" type="number" min={0} max={1000} name={key} defaultValue={Number(settings[key] ?? 0)} required />
                      <span className="color font-11">{hint}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <hr className="dotted" />
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-3">
              <h5 style={{ marginBottom: 20 }}>What 100 means</h5>
              <p className="color font-12">Not &quot;what is good&quot;, but the point from which a factor scores full marks. A machine at half of every target lands around 50 - right in the middle, which is where it reads.</p>
            </div>
            <div className="col-md-9">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Processor: cores &times; GHz</label>
                    <input className="form-control" type="number" min={1} max={1000} step={0.5} name="target_cpu" defaultValue={Number(settings.target_cpu ?? 0)} required />
                    <span className="color font-11">default {defaults.target_cpu} (8 cores at 3 GHz)</span>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Memory</label>
                    <div className="input-group">
                      <input className="form-control" type="number" min={1} max={2048} name="target_ram_gb" defaultValue={Number(settings.target_ram_gb ?? 0)} required />
                      <span className="input-group-addon">GB</span>
                    </div>
                    <span className="color font-11">default {defaults.target_ram_gb}</span>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Age at which the factor reaches zero</label>
                    <div className="input-group">
                      <input className="form-control" type="number" min={1} max={50} name="max_age_years" defaultValue={Number(settings.max_age_years ?? 0)} required />
                      <span className="input-group-addon">years</span>
                    </div>
                    <span className="color font-11">default {defaults.max_age_years}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr className="dotted" />
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-3">
              <h5 style={{ marginBottom: 20 }}>Where the bands break</h5>
              <p className="color font-12">
                The same words that appear on the device page. Below the first threshold it is <strong>Very Slow</strong> (red), above the last one <strong>Fast</strong> (green), and in between <strong>Slow</strong> (yellow) and <strong>Fair</strong> (blue).
              </p>
              <p className="color font-12">
                Two equal thresholds erase the band between them: with 40, 70 and 70 you get three colours - red below 40, yellow up to 70, green from 70 up. That is how it comes out of the box.
              </p>
            </div>
            <div className="col-md-9">
              <div className="row">
                {(
                  [
                    ['band_poor', 'Below this: Very Slow'],
                    ['band_slow', 'Below this: Slow'],
                    ['band_fair', 'Below this: Fair, above: Fast'],
                  ] as const
                ).map(([key, label]) => (
                  <div className="col-md-4" key={key}>
                    <div className="form-group">
                      <label>{label}</label>
                      <input className="form-control" type="number" min={1} max={100} name={key} defaultValue={Number(settings[key] ?? 0)} required />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <hr className="dotted" />
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-3">
              <h5 style={{ marginBottom: 20 }}>How the estate falls out</h5>
              <p className="color font-12">The agent-based machines of the customer you have open, scored with the settings above. Recalculated on every save.</p>
            </div>
            <div className="col-md-9">
              <div id="scoreDistribution">
                {([4, 3, 2, 1, 'unknown'] as const).map((band) => {
                  const look = bands[String(band)] ?? { name: 'Not scored', label: 'label-default' }
                  return (
                    <label className={`label od-inv-band ${look.label}`} key={String(band)}>
                      {look.name}: <span data-band={band}>{Number(page.data.distribution[String(band)] ?? 0)}</span>
                    </label>
                  )
                })}
              </div>
              <p className="color font-11" style={{ marginTop: 10 }}>
                &bdquo;Not scored&rdquo; are the machines that reported none of what is measured - not the bad ones. They get no mark, so they are not blamed for the agent&apos;s silence.
              </p>
            </div>
          </div>
        </section>
        <div className="form-group" style={{ marginTop: 20 }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={!page.data.available}>
            Save
          </button>
        </div>
      </form>
    </>
  )
}

/*
 * Gravitatea unei alarme, in cuvinte si in pastila consolei. Acelasi inteles
 * ca pe Relation rules: tenta palida, scris inchis, si niciodata culoare tare
 * sub cerneala.
 */
const ALARM_SEVERITY: Array<{ value: string; label: string; pill: string }> = [
  { value: '0', label: 'Info', pill: 'label-info' },
  { value: '1', label: 'Minor', pill: 'label-success' },
  { value: '2', label: 'Major', pill: 'label-warning' },
  { value: '3', label: 'Critical', pill: 'label-danger' },
]

function severityOf(flag: number): { value: string; label: string; pill: string } {
  return ALARM_SEVERITY[flag] ?? ALARM_SEVERITY[0]
}

/*
 * Ce fel de alarma se deschide cand omul n-a ales inca nimic.
 *
 * In portal coloana din stanga nu ramanea goala: `DefaultAlarmsInventoree`
 * apasa singur primul rand -
 * `$('#default-alarms-nav').find('li.nav-tab').first().children('a').trigger('click')`
 * - si pragurile se vedeau de la deschidere. Rescrierea pierduse pasul, iar
 * ecranul se deschidea cu nimic ales si cu un indemn in locul listei.
 *
 * ADRESA BATE IMPLICITUL: `?type=10` - sau `?alerttype=10`, cum scrie si
 * atributul randului - deschide chiar felul ala, ca sa poata fi trimis un link
 * catre un prag anume.
 */
function alarmTypeFromUrl(): number {
  if (typeof window === 'undefined') return 0
  const query = new URLSearchParams(window.location.search)
  return Number(query.get('type') ?? query.get('alerttype') ?? 0) || 0
}

/** Primul fel de alarma din prima grupa care are unul. 0 daca nu e niciunul. */
function firstAlarmType(categories: Array<Record<string, unknown> & { alert_types?: Array<Record<string, unknown>> }>): number {
  for (const category of categories) {
    for (const alert of category.alert_types ?? []) {
      const id = num(alert, 'ID')
      if (id) return id
    }
  }
  return 0
}

function AlarmsPanel() {
  const page = useQuery(alarmsQuery())
  /* Ce a ales omul. 0 = inca nimic, si atunci hotaraste adresa sau lista. */
  const [picked, setPicked] = useState(0)
  const [pack, setPack] = useState<{ default: boolean; alertType: Record<string, unknown> | null; alarms: Array<Record<string, unknown>> } | null>(null)
  const [edit, setEdit] = useState<Record<string, unknown> | null>(null)
  const [creating, setCreating] = useState(false)
  /* Gravitatea aleasa in cele doua formulare: `SelectPicker` e o lista
     stapanita, iar `<select>`-ul ascuns de sub ea duce valoarea in POST. */
  const [editSeverity, setEditSeverity] = useState('2')
  const [newSeverity, setNewSeverity] = useState('2')
  /* Felul deschis acum: alegerea omului, apoi adresa, apoi primul din lista.
     Nu e o stare, ci se socoteste la desenare - deci randul e aprins din
     prima desenare in care vin categoriile, fara o trecere prin "nimic ales"
     si fara o a doua cerere ca sa se dreaga. */
  const type = picked || alarmTypeFromUrl() || firstAlarmType(page.data?.categories ?? [])
  /* O singura cerere pentru fiecare fel deschis. Fara nicio categorie, `type`
     e 0 si nu se cere nimic: ecranul ramane intreg si nu intreaba in bucla. */
  useEffect(() => {
    if (type === 0) return
    let alive = true
    void getAlarms(type).then((next) => {
      if (alive) setPack(next)
    })
    return () => {
      alive = false
    }
  }, [type])
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  /* Alegerea altui fel schimba doar starea; cererea o face efectul de sus. */
  const load = (id: number) => {
    setPicked(id)
    setEdit(null)
    setCreating(false)
  }
  /* Aceeasi lista, citita din nou dupa o schimbare: `type` nu se misca, deci
     efectul nu se trezeste singur. */
  const reload = () => {
    void getAlarms(type).then(setPack)
  }
  return (
    <>
      {/*
       * Uneltele stau in capul ecranului, ca pe celelalte zece. Inainte
       * "Load default settings" era scris deasupra listei si "Create alarm"
       * dedesubtul ei - masurat in Edge cu foile de productie, amandoua
       * cadeau PESTE lista (-28px si -163px).
       */}
      <InvHead
        strong="Alarms"
        rest="settings"
        subtitle={
          <>
            Create and edit INVENTOREE <strong>alarms</strong>
          </>
        }
        tools={
          pack ? (
            <>
              {pack.default && (
                <button type="button" className="btn btn-default btn-sm" onClick={() => void loadDefaultSettings(type).then(() => reload())}>
                  Load default settings
                </button>
              )}
              <button type="button" className="btn btn-success btn-sm" onClick={() => setCreating(true)}>
                <i className="fas fa-plus-circle"></i> Create alarm
              </button>
            </>
          ) : undefined
        }
      />
      <div id="default-alarms-content" className="row">
        {/*
         * Coloana de alegere a consolei, aceeasi ca pe Software Licenses. Era
         * un `well` gri (#f5f5f5, scris inline) cu `nav-pills` in el, si doua
         * douasprezecimi latime - adica 122px pentru nume ca "Disk free
         * space", care se rupeau in trei randuri.
         */}
        <div className="col-md-3">
          <InvPicker id="default-alarms-nav">
            {page.data.categories.map((category) => (
              <AlarmCategory key={num(category, 'ID')} category={category} current={type} onPick={load} />
            ))}
          </InvPicker>
        </div>
        <div className="col-md-9" id="alarms-nav-container">
          {!pack && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle"></i> Pick an alarm type on the left to see its thresholds.
            </div>
          )}
          {pack && (
            <>
              {/*
               * Randul pe coloane, ca pe toate listele din Inventoree. Inainte
               * era text, apoi doua butoane `pull-right` in ordine INVERSA
               * (Remove iesea la stanga lui Edit, fiindca a doua plutire se
               * aseaza mai la stanga), si abia la urma bifa - care in cod sta
               * dupa butoane, deci ajungea dupa ele si pe ecran.
               */}
              <ul className="list-group od-inv-list">
                {pack.alarms.length === 0 ? (
                  <li className="list-group-item" style={{ padding: 0, border: 0 }}>
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> No thresholds for this alarm type yet.
                    </div>
                  </li>
                ) : (
                  pack.alarms.map((alarm) => {
                    const severity = severityOf(num(alarm, 'flag'))
                    return (
                      <li className="list-group-item od-inv-row" key={num(alarm, 'ID')}>
                        <div className="od-inv-name">
                          {str(alarm, 'from_value')} &ndash; {str(alarm, 'to_value')}
                        </div>
                        <div className="od-inv-count">
                          <span className={`label ${severity.pill}`}>{severity.label}</span>
                        </div>
                        <div className="od-inv-status">
                          <InvCheck
                            className="icheck-alarm"
                            name="activate"
                            label="Enabled"
                            checked={num(alarm, 'status') === 1}
                            onChange={(on) => void changeAlarmStatus(num(alarm, 'ID'), on ? 1 : 0).then(() => reload())}
                          />
                        </div>
                        <div className="od-inv-actions">
                          <button
                            type="button"
                            className="btn btn-xs btn-primary"
                            onClick={() => {
                              setEditSeverity(String(num(alarm, 'flag')))
                              setEdit(alarm)
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-danger"
                            onClick={() => {
                              if (!confirm('Are you sure?')) return
                              void removeAlarm(num(alarm, 'ID')).then(() => reload())
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    )
                  })
                )}
              </ul>
              {edit && (
                <form
                  className="repeater portlet"
                  id="edit-default-alarm-form"
                  method="post"
                  onSubmit={(e) => {
                    e.preventDefault()
                    const form = new FormData(e.currentTarget)
                    const fields: Record<string, string> = {}
                    form.forEach((v, k) => {
                      fields[k] = String(v)
                    })
                    void updateAlarm(fields).then(() => {
                      setEdit(null)
                      reload()
                    })
                  }}
                >
                  <h4>Edit alarm</h4>
                  <input type="hidden" name="alarm-type" value={str(edit, 'alert_type')} />
                  <input type="hidden" name="alarm-id" value={num(edit, 'ID')} />
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="editAlarmSeverity">Severity</label>
                        <SelectPicker
                          id="editAlarmSeverity"
                          name="alarm-severity"
                          title="Select.."
                          value={editSeverity}
                          onChange={setEditSeverity}
                          options={ALARM_SEVERITY.map((row) => ({ value: row.value, label: row.label }))}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="editAlarmFrom">From</label>
                        <input className="form-control" id="editAlarmFrom" name="minThreshold" type="number" defaultValue={str(edit, 'from_value')} required />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="editAlarmTo">To</label>
                        <input className="form-control" id="editAlarmTo" name="maxThreshold" type="number" defaultValue={str(edit, 'to_value')} required />
                      </div>
                    </div>
                  </div>
                  {/* Cele doua bife: eticheta sta langa casuta, nu sub ea. */}
                  <div className="form-group od-inv-checks">
                    <InvCheck className="icheck-alarm" name="activate" value="on" label="Activate" defaultChecked={num(edit, 'status') === 1} />
                    <InvCheck className="icheck-alarm" name="send_email" value="on" label="Send email to admin" defaultChecked={num(edit, 'actions') === 1} />
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm">
                    Save
                  </button>{' '}
                  <button type="button" className="btn btn-default btn-sm" onClick={() => setEdit(null)}>
                    Cancel
                  </button>
                </form>
              )}
              {creating && (
                <form
                  className="portlet"
                  id="create-default-alarm-form"
                  method="post"
                  onSubmit={(e) => {
                    e.preventDefault()
                    const form = new FormData(e.currentTarget)
                    const fields: Record<string, string> = {}
                    form.forEach((v, k) => {
                      fields[k] = String(v)
                    })
                    void createAlarm(fields).then(() => {
                      setCreating(false)
                      reload()
                    })
                  }}
                >
                  <h4>New alarm</h4>
                  <input type="hidden" name="alarm-type" value={type} />
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="newAlarmSeverity">Severity</label>
                        <SelectPicker
                          id="newAlarmSeverity"
                          name="alarm-severity"
                          title="Select.."
                          value={newSeverity}
                          onChange={setNewSeverity}
                          options={ALARM_SEVERITY.map((row) => ({ value: row.value, label: row.label }))}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="newAlarmFrom">From</label>
                        <input className="form-control" id="newAlarmFrom" name="minThreshold" type="number" required />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="newAlarmTo">To</label>
                        <input className="form-control" id="newAlarmTo" name="maxThreshold" type="number" required />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm">
                    Create
                  </button>{' '}
                  <button type="button" className="btn btn-default btn-sm" onClick={() => setCreating(false)}>
                    Cancel
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

function AlarmCategory({
  category,
  current,
  onPick,
}: {
  category: Record<string, unknown> & { alert_types?: Array<Record<string, unknown>> }
  /** Ce fel de alarma e deschis acum - randul lui se aprinde. */
  current: number
  onPick(id: number): void
}) {
  const types = category.alert_types ?? []
  return (
    <>
      <InvPickerHeader label={str(category, 'category_name')} />
      {types.map((alert) => (
        <InvPickerItem
          key={num(alert, 'ID')}
          label={str(alert, 'type_name')}
          active={num(alert, 'ID') === current}
          onPick={() => onPick(num(alert, 'ID'))}
          aProps={{ 'data-alerttype': num(alert, 'ID') }}
        />
      ))}
    </>
  )
}

/*
 * „Endpoint Performance Status". Ecranul e scris aici si de aici isi ia
 * capetele, dar randul din meniu sta in Settings › Opteemum de pe 16 septembrie
 * 2026, si tot acolo se si deschide: `OpteemumSettingsPage` deseneaza chiar
 * panoul asta. Se exporta ca sa ramana UN SINGUR panou, folosit din amandoua
 * paginile - o a doua copie ar pleca in alta parte la prima reparatie.
 */
export function EndpointPanel() {
  const query = useQueryClient()
  const page = useQuery(endpointQuery())
  const ips = useQuery(endpointIpsQuery())
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  /* O cerere picata lasa `data` gol pentru totdeauna: fara randul asta,
     spinnerul de mai jos s-ar invarti fara capat. */
  if (page.error) {
    return <LoadFailed what="The endpoint status settings" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />
  /* Prin `obj()`, nu de-a dreptul: raspunsul a avut `connections: null`. */
  const settings = obj(page.data.settings)
  const connections = obj(page.data.connections)
  const list = Array.isArray(ips.data?.ips) ? ips.data.ips : []
  return (
    <>
      <h4>
        <strong>Endpoint status</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>endpoint status</strong>
      </label>
      <hr />
      <form
        action=""
        method="post"
        id="updateEndpointAlarmForm"
        onSubmit={(e) => {
          e.preventDefault()
          const form = new FormData(e.currentTarget)
          const fields: Record<string, string> = {}
          form.forEach((v, k) => {
            fields[k] = String(v)
          })
          void updateEndpoint(fields).then((res) => {
            if (res.status === 'error') toast(res.message ?? 'Error', 'Error', 'error')
            else toast('Data saved!', 'Success')
            void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'endpoint'] })
          })
        }}
      >
        <input type="hidden" name="company" value={Number(page.data.company ?? 0)} />
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Network Connection</h5>
            </div>
            <div className="col-md-10">
              <div className="form-group">
                {/*
                 * Bifa zonei. Inainte era `.checkbox > label > input`: forma in
                 * care Bootstrap scoate casuta din flux (`position: absolute`
                 * cu `margin-left: -20px`), si fara nicio regula de zona care
                 * s-o aduca inapoi statea chiar peste primul cuvant al
                 * etichetei. `icheck-radio` era numele unui plugin care in
                 * /app/ nici nu se incarca.
                 */}
                <InvCheck
                  name="connection_network"
                  label="Enable network connection"
                  defaultChecked={num(connections, 'connection_network') === 1}
                />
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Processor</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div className="input-group">
                      <input className="form-control" name="maxValueProcessor" type="number" defaultValue={num(obj(settings.processor), 'max_value')} required />
                      <span className="input-group-addon">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Ram</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div className="input-group">
                      <input className="form-control" name="maxValueRam" type="number" defaultValue={num(obj(settings.ram), 'max_value')} required />
                      <span className="input-group-addon">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Swap</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div className="input-group">
                      <input className="form-control" name="maxValueSwap" type="number" defaultValue={num(obj(settings.swap), 'max_value')} required />
                      <span className="input-group-addon">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Response Interval</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div className="input-group">
                      <input type="number" className="form-control" id="response_time" name="response_time" defaultValue={num(connections, 'response_time')} required />
                      <span className="input-group-addon">Min</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr className="dotted" />
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-12 align-left">
              <button type="submit" className="btn btn-sm btn-primary">
                <i className="fa fa-check"></i> Save
              </button>
            </div>
          </div>
        </section>
      </form>
      <form
        id="new-ip-list"
        method="post"
        encType="multipart/form-data"
        action=""
        onSubmit={(e) => {
          e.preventDefault()
          const form = new FormData(e.currentTarget)
          const fields: Record<string, string> = {}
          form.forEach((v, k) => {
            fields[k] = String(v)
          })
          void addIp(fields).then((res) => {
            if (res.status === 'error') toast(res.message ?? 'Error', 'Error', 'error')
            else void query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'endpoint-ips'] })
          })
        }}
      >
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Network Adress</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div>
                      <input type="text" className="form-control" id="ip" name="ip" required />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Port</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div className="input-group">
                      <input type="number" className="form-control" id="port" name="port" required />
                      <span className="input-group-addon">Port</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Response time</h5>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <div className="row">
                  <div className="col-md-3">
                    <div className="input-group">
                      <input className="form-control" name="maxValuePortResponse" type="number" required />
                      <span className="input-group-addon">ms</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr className="dotted" />
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-12 align-left">
              <button type="submit" className="btn btn-sm btn-primary">
                <i className="fa fa-check"></i> Save
              </button>
            </div>
          </div>
        </section>
      </form>
      <div className="row" style={{ paddingTop: 30 }}>
        <div className="col-sm-4">
          {/*
           * Adresele urmarite: lista zonei, nu un `<ul>` gol intr-un chenar
           * gri desenat inline (#d3cfcf) - singurul din tot modulul. Randul e
           * cel din Inventoree: numele la stanga, butonul in coloana lui.
           */}
          <ul id="ip_list" className="list-group od-inv-list od-inv-scroll">
            {ips.error ? (
              /*
               * O lista care a picat nu se da drept lista goala: "nicio adresa"
               * ar fi o minciuna, si omul ar adauga a doua oara ce e deja acolo.
               */
              <li className="list-group-item" style={{ padding: 0, border: 0 }}>
                <LoadFailed what="The watched addresses" error={ips.error} onRetry={() => void ips.refetch()} />
              </li>
            ) : list.length === 0 ? (
              <li className="list-group-item" style={{ padding: 0, border: 0 }}>
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> No addresses watched yet.
                </div>
              </li>
            ) : (
              list.map((row) => (
                <li className="list-group-item od-inv-row" key={num(row, 'ID')}>
                  <div className="od-inv-name">
                    {str(row, 'ip')}:{str(row, 'port')}
                  </div>
                  <div className="od-inv-actions">
                    <button
                      type="button"
                      className="btn btn-xs btn-danger"
                      onClick={() => void removeIp(num(row, 'ID')).then(() => query.invalidateQueries({ queryKey: ['settings', 'inventoree', 'endpoint-ips'] }))}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </>
  )
}